import React, {useEffect, useRef, useState} from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import TabPanel from 'components/shared/TabPanel';
import SalesByHours from 'components/feature/Tabs/SalesByHours';
import SalesByItems from 'components/feature/Tabs/SalesByItems';
import SalesPerHourLegend from 'components/feature/Tabs/SalesPerHourLegend';
import {
  DashboardChannelSelect,
  DashboardDatepicker,
  DashboardInnerBody,
  DashboardInnerDescription,
  DashboardInnerHeader,
  DashboardInnerHeaderMeta,
  DashboardInners,
  DashboardSelectWrapper,
  DashboardTabs
} from 'routes/App/dashboard/styles';
import {DashboardInner} from 'components/DashboardInner/styles';
import Headline from 'components/Headline/styles';
import SubTitle from 'components/SubTitle/styles';
import ButtonInfo from 'components/buttons/ButtonInfo/styles';
import TotalSales from 'components/feature/Tabs/TotalSales';
import TotalCustomers from 'components/feature/Tabs/TotalCustomers';
import {
  useDatePickerContext,
  useDatePickerContextUpdate
} from 'components/Datepicker/DatePickerContext';
import {
  AnalyticsFiltersProvider,
  useAnalyticsFilters
} from 'components/AnalyticsFilters/AnalyticsFiltersContext';
import AnalyticsChannelPicker from 'components/AnalyticsFilters/AnalyticsChannelPicker';
import AnalyticsFulfillmentPicker from 'components/AnalyticsFilters/AnalyticsFulfillmentPicker';
import DatepickerModule from 'components/DatepickerModule';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import {
  ANALYTICS_DATEPICKER_INDEX,
  ANALYTICS_ITEMS_PAGE_SIZE,
  ANALYTICS_SORT_FIELDS
} from 'helpers/constants/analytics';
import {
  getAnalyticsV3Api,
  getAnalyticsV3SalesPerItemApi
} from 'helpers/services/api/analytics';
import {
  ANALYTICS_ALL_PERIOD_INDEX,
  ANALYTICS_DATE_PERIOD_STORAGE_NAME,
  emptySalesPerHourGroup,
  hasAnalyticsDateRange,
  normalizeCustomersCount,
  normalizeSalesCount,
  normalizeSalesPerHour,
  normalizeSalesPerItem,
  resolveAnalyticsRequestDates,
  unwrapAnalyticsPayload
} from 'helpers/services/api/analyticsHelpers';
import {
  getAnalyticsTodayRange,
  getAnalyticsYesterdayRange,
  getLocationWallClockNow
} from 'helpers/functionUtils/analyticsBusinessDayRange';
import {setStorageDate} from 'helpers/localStorage/DateStorage';
import {
  getStoragePeriod,
  setStoragePeriod
} from 'helpers/localStorage/DatePeriodStorage';
import {setCustomRange} from 'helpers/localStorage/CustomRangeStorage';

const ANALYTICS_DATE_STORAGE_NAME = 'Date';
const ANALYTICS_CUSTOM_RANGE_STORAGE_NAME = 'CustomRange';
const ANALYTICS_DEFAULT_PERIOD_INDEX = ANALYTICS_DATEPICKER_INDEX.THIS_WEEK;
const ANALYTICS_CUSTOM_RANGE_PERIOD_INDEX =
  ANALYTICS_DATEPICKER_INDEX.CUSTOM_RANGE;

const SalesPerHourHeader = ({title, grouping, seriesOverride}) => {
  const storeSeries = useSelector(
    (state) => state.analyticsSalesPerHourReducer[grouping]?.series
  );
  const series = seriesOverride ?? storeSeries ?? [];

  return (
    <DashboardInnerHeader>
      <SubTitle>{title}</SubTitle>
      <DashboardInnerHeaderMeta>
        <SalesPerHourLegend series={series} />
        <DashboardInnerDescription>
          Average Hourly Sales Number
        </DashboardInnerDescription>
      </DashboardInnerHeaderMeta>
    </DashboardInnerHeader>
  );
};

SalesPerHourHeader.propTypes = {
  title: PropTypes.string.isRequired,
  grouping: PropTypes.oneOf(['perFulfillment', 'perChannel']).isRequired,
  seriesOverride: PropTypes.arrayOf(PropTypes.object)
};

SalesPerHourHeader.defaultProps = {
  seriesOverride: null
};

const ALL_ANALYTICS_DEFAULT_ORDER_BY = 'totalOrders';
const ALL_ANALYTICS_DEFAULT_ORDER = 'desc';

const EMPTY_ALL_ANALYTICS = {
  salesCount: {},
  customersCount: {},
  salesPerHour: {
    perFulfillment: emptySalesPerHourGroup(),
    perChannel: emptySalesPerHourGroup()
  }
};

const EMPTY_SALES_PER_ITEM = {
  items: [],
  totalCount: 0
};

const AllAnalyticsPanel = ({isDateReady}) => {
  const date = useDatePickerContext();
  const {channelTypes, fulfillmentModes} = useAnalyticsFilters();
  const {locationId} = useSelector((state) => state.user);
  const latestDashboardRequestRef = useRef(0);
  const latestItemsRequestRef = useRef(0);

  const [dashboard, setDashboard] = useState(EMPTY_ALL_ANALYTICS);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [salesPerItem, setSalesPerItem] = useState(EMPTY_SALES_PER_ITEM);
  const [salesPerItemLoading, setSalesPerItemLoading] = useState(false);
  const [salesPerItemPage, setSalesPerItemPage] = useState(0);
  const [salesPerItemOrder, setSalesPerItemOrder] = useState(
    ALL_ANALYTICS_DEFAULT_ORDER
  );
  const [salesPerItemOrderBy, setSalesPerItemOrderBy] = useState(
    ALL_ANALYTICS_DEFAULT_ORDER_BY
  );

  useEffect(() => {
    setSalesPerItemPage(0);
  }, [
    date,
    locationId,
    channelTypes,
    fulfillmentModes,
    salesPerItemOrderBy,
    salesPerItemOrder
  ]);

  // KPIs + per-hour chart: reloads only when date/location/filters change.
  useEffect(() => {
    if (!isDateReady || !locationId || !hasAnalyticsDateRange(date)) {
      return undefined;
    }

    const requestId = latestDashboardRequestRef.current + 1;
    latestDashboardRequestRef.current = requestId;

    const loadDashboard = async () => {
      setDashboardLoading(true);

      try {
        const {startDate, endDate} = resolveAnalyticsRequestDates(date);
        const response = await getAnalyticsV3Api(locationId, {
          startDate,
          endDate,
          channelTypes,
          fulfillmentModes,
          includeSalesPerItem: false,
          includeKpi: true,
          includePerHoursStatistics: true
        });

        if (latestDashboardRequestRef.current !== requestId) {
          return;
        }

        const payload = unwrapAnalyticsPayload(response);
        setDashboard({
          salesCount: normalizeSalesCount(payload),
          customersCount: normalizeCustomersCount(payload),
          salesPerHour: normalizeSalesPerHour(payload)
        });
      } catch (error) {
        if (latestDashboardRequestRef.current !== requestId) {
          return;
        }

        setDashboard(EMPTY_ALL_ANALYTICS);
      } finally {
        if (latestDashboardRequestRef.current === requestId) {
          setDashboardLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      // Invalidate this request so a late response can't set state after
      // the effect re-runs or the component unmounts.
      latestDashboardRequestRef.current = requestId + 1;
    };
  }, [isDateReady, date, locationId, channelTypes, fulfillmentModes]);

  // Sales per item goes through the standalone v3 endpoint so paging and
  // sorting never reload the KPIs and the chart.
  useEffect(() => {
    if (!isDateReady || !locationId || !hasAnalyticsDateRange(date)) {
      return undefined;
    }

    const requestId = latestItemsRequestRef.current + 1;
    latestItemsRequestRef.current = requestId;

    const loadSalesPerItem = async () => {
      setSalesPerItemLoading(true);

      try {
        const sortingKey =
          ANALYTICS_SORT_FIELDS[salesPerItemOrderBy] ??
          salesPerItemOrderBy ??
          'rank';
        const {startDate, endDate} = resolveAnalyticsRequestDates(date);
        const response = await getAnalyticsV3SalesPerItemApi(locationId, {
          startDate,
          endDate,
          channelTypes,
          fulfillmentModes,
          paging: salesPerItemPage + 1,
          pageSize: ANALYTICS_ITEMS_PAGE_SIZE,
          sortingKey,
          sortingDirection: salesPerItemOrder
        });

        if (latestItemsRequestRef.current !== requestId) {
          return;
        }

        setSalesPerItem(
          normalizeSalesPerItem(
            unwrapAnalyticsPayload(response),
            ANALYTICS_ITEMS_PAGE_SIZE
          )
        );
      } catch (error) {
        if (latestItemsRequestRef.current !== requestId) {
          return;
        }

        setSalesPerItem(EMPTY_SALES_PER_ITEM);
      } finally {
        if (latestItemsRequestRef.current === requestId) {
          setSalesPerItemLoading(false);
        }
      }
    };

    loadSalesPerItem();

    return () => {
      // Invalidate this request so a late response can't set state after
      // the effect re-runs or the component unmounts.
      latestItemsRequestRef.current = requestId + 1;
    };
  }, [
    isDateReady,
    date,
    locationId,
    channelTypes,
    fulfillmentModes,
    salesPerItemPage,
    salesPerItemOrderBy,
    salesPerItemOrder
  ]);

  return (
    <>
      <DashboardInners>
        <DashboardInner className='DashboardInnerW50'>
          <DashboardInnerHeader>
            <SubTitle>Sales Count</SubTitle>
          </DashboardInnerHeader>
          <DashboardInnerBody className='WithoutMargin'>
            <TotalSales
              disableFetch
              dataOverride={dashboard.salesCount}
              loadingOverride={dashboardLoading}
            />
          </DashboardInnerBody>
        </DashboardInner>
        <DashboardInner className='DashboardInnerW50'>
          <DashboardInnerHeader>
            <SubTitle>Customers Count</SubTitle>
          </DashboardInnerHeader>
          <DashboardInnerBody className='WithoutMargin'>
            <TotalCustomers
              disableFetch
              dataOverride={dashboard.customersCount}
              loadingOverride={dashboardLoading}
            />
          </DashboardInnerBody>
        </DashboardInner>
      </DashboardInners>
      <DashboardInner>
        <SalesPerHourHeader
          title='Sales per Hour per Fulfillment'
          grouping='perFulfillment'
          seriesOverride={dashboard.salesPerHour.perFulfillment.series}
        />
        <DashboardInnerBody>
          <SalesByHours
            grouping='perFulfillment'
            disableFetch
            dataOverride={dashboard.salesPerHour.perFulfillment.hours}
            seriesOverride={dashboard.salesPerHour.perFulfillment.series}
            avgSalesOverride={dashboard.salesPerHour.perFulfillment.avgSales}
            loadingOverride={dashboardLoading}
          />
        </DashboardInnerBody>
      </DashboardInner>
      <DashboardInner>
        <SalesPerHourHeader
          title='Sales per Hour per Channel'
          grouping='perChannel'
          seriesOverride={dashboard.salesPerHour.perChannel.series}
        />
        <DashboardInnerBody>
          <SalesByHours
            grouping='perChannel'
            disableFetch
            dataOverride={dashboard.salesPerHour.perChannel.hours}
            seriesOverride={dashboard.salesPerHour.perChannel.series}
            avgSalesOverride={dashboard.salesPerHour.perChannel.avgSales}
            loadingOverride={dashboardLoading}
          />
        </DashboardInnerBody>
      </DashboardInner>
      <DashboardInner>
        <DashboardInnerHeader
          style={{
            justifyContent: 'flex-start'
          }}
        >
          <SubTitle>Sales per Item</SubTitle>
          <ButtonInfo
            className='Outline ButtonInfoDrop'
            style={{
              marginLeft: '11px'
            }}
          >
            <div className='DropInfo DropInfoCenter Small'>
              The rank and performance of your items based on Sales per Item
              (Orders) and by Generated Revenue per Item (Sales)
            </div>
          </ButtonInfo>
        </DashboardInnerHeader>
        <DashboardInnerBody>
          <SalesByItems
            disableFetch
            itemsOverride={salesPerItem.items}
            totalCountOverride={salesPerItem.totalCount}
            loadingOverride={salesPerItemLoading}
            currentPageOverride={salesPerItemPage}
            orderOverride={salesPerItemOrder}
            orderByOverride={salesPerItemOrderBy}
            onPageChangeOverride={setSalesPerItemPage}
            onSortChangeOverride={({order, orderBy}) => {
              setSalesPerItemOrder(order);
              setSalesPerItemOrderBy(orderBy);
            }}
          />
        </DashboardInnerBody>
      </DashboardInner>
    </>
  );
};

AllAnalyticsPanel.propTypes = {
  isDateReady: PropTypes.bool.isRequired
};

const getAnalyticsDateRangeForPeriod = ({periodIndex, Timezone}) => {
  const locationDate = () => getLocationWallClockNow(Timezone);

  switch (periodIndex) {
    case ANALYTICS_DATEPICKER_INDEX.TODAY:
      return getAnalyticsTodayRange({Timezone});
    case ANALYTICS_DATEPICKER_INDEX.YESTERDAY:
      return getAnalyticsYesterdayRange({Timezone});
    case ANALYTICS_DATEPICKER_INDEX.THIS_WEEK:
      return [
        locationDate().utc().startOf('isoWeek').format(),
        locationDate().utc().endOf('isoWeek').format()
      ];
    case ANALYTICS_DATEPICKER_INDEX.LAST_WEEK:
      return [
        locationDate().utc().subtract(1, 'week').startOf('isoWeek').format(),
        locationDate().utc().subtract(1, 'week').endOf('isoWeek').format()
      ];
    case ANALYTICS_DATEPICKER_INDEX.THIS_MONTH:
      return [
        locationDate().utc().startOf('month').format(),
        locationDate().utc().endOf('month').format()
      ];
    case ANALYTICS_DATEPICKER_INDEX.LAST_MONTH:
      return [
        locationDate().utc().subtract(1, 'month').startOf('month').format(),
        locationDate().utc().subtract(1, 'month').endOf('month').format()
      ];
    default:
      return [
        locationDate().utc().startOf('isoWeek').format(),
        locationDate().utc().endOf('isoWeek').format()
      ];
  }
};

const Dashboard = () => {
  const [value, setValue] = useState(0);
  const date = useDatePickerContext();
  const updateDate = useDatePickerContextUpdate();
  const Timezone = useSelector((state) => state.user.activeLocation?.Timezone);
  const [isDateReady, setIsDateReady] = useState(() => {
    const savedPeriod = getStoragePeriod(ANALYTICS_DATE_PERIOD_STORAGE_NAME);

    return (
      savedPeriod === ANALYTICS_CUSTOM_RANGE_PERIOD_INDEX ||
      savedPeriod === ANALYTICS_ALL_PERIOD_INDEX
    );
  });

  useEffect(() => {
    if (Timezone == null) {
      return;
    }

    const savedPeriod = getStoragePeriod(ANALYTICS_DATE_PERIOD_STORAGE_NAME);

    // Custom range keeps previously chosen dates
    if (savedPeriod === ANALYTICS_CUSTOM_RANGE_PERIOD_INDEX) {
      setIsDateReady(true);
      return;
    }

    // All period — keep storage/context as-is, requests send null by period
    if (savedPeriod === ANALYTICS_ALL_PERIOD_INDEX) {
      setIsDateReady(true);
      return;
    }

    const periodIndex =
      savedPeriod !== undefined && savedPeriod !== null
        ? savedPeriod
        : ANALYTICS_DEFAULT_PERIOD_INDEX;

    const periodCurrentDate = getAnalyticsDateRangeForPeriod({
      periodIndex,
      Timezone
    });

    updateDate(periodCurrentDate);
    setStorageDate(ANALYTICS_DATE_STORAGE_NAME, periodCurrentDate);
    setCustomRange(ANALYTICS_CUSTOM_RANGE_STORAGE_NAME, []);
    setStoragePeriod(ANALYTICS_DATE_PERIOD_STORAGE_NAME, periodIndex);
    setIsDateReady(true);
  }, [Timezone]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <AnalyticsFiltersProvider>
      <DashboardContent className='DashboardContent'>
        <>
          <DashboardHeader>
            <Headline className='mTop5'>Analytics</Headline>
          </DashboardHeader>
          <DashboardTabs className='DashboardTabsAnalytics'>
            <Tabs value={value} onChange={handleChange} elevation={0}>
              <Tab label='All Analytics' />
              <Tab label='Sales Count' />
              <Tab label='Customers Count' />
              <Tab label='Sales per Hour' />
              <Tab label='Sales per Item' />
            </Tabs>
          </DashboardTabs>
          <DashboardSelectWrapper>
            <DashboardDatepicker>
              <DatepickerModule
                date={date}
                updateDate={updateDate}
                defaultSelectIndex={ANALYTICS_DEFAULT_PERIOD_INDEX}
                showYesterday
                localStorageObject={{
                  storageDateName: ANALYTICS_DATE_STORAGE_NAME,
                  storageCustomRangeName: ANALYTICS_CUSTOM_RANGE_STORAGE_NAME,
                  storageDatePeriodName: ANALYTICS_DATE_PERIOD_STORAGE_NAME
                }}
              />
            </DashboardDatepicker>
            <DashboardChannelSelect>
              <AnalyticsChannelPicker />
            </DashboardChannelSelect>
            <DashboardChannelSelect>
              <AnalyticsFulfillmentPicker />
            </DashboardChannelSelect>
          </DashboardSelectWrapper>

          <TabPanel value={value} index={0}>
            <AllAnalyticsPanel isDateReady={isDateReady} />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <DashboardInner>
              <DashboardInnerHeader>
                <SubTitle>Sales Count</SubTitle>
              </DashboardInnerHeader>
              <DashboardInnerBody className='WithoutMargin'>
                {isDateReady && <TotalSales />}
              </DashboardInnerBody>
            </DashboardInner>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <DashboardInner>
              <DashboardInnerHeader>
                <SubTitle>Customers Count</SubTitle>
              </DashboardInnerHeader>
              <DashboardInnerBody className='WithoutMargin'>
                {isDateReady && <TotalCustomers />}
              </DashboardInnerBody>
            </DashboardInner>
          </TabPanel>
          <TabPanel value={value} index={3}>
            <DashboardInner>
              <SalesPerHourHeader
                title='Sales per Hour per Fulfillment'
                grouping='perFulfillment'
              />
              <DashboardInnerBody>
                {isDateReady && <SalesByHours grouping='perFulfillment' />}
              </DashboardInnerBody>
            </DashboardInner>
            <DashboardInner>
              <SalesPerHourHeader
                title='Sales per Hour per Channel'
                grouping='perChannel'
              />
              <DashboardInnerBody>
                {/* One request fills both groupings — only the first chart fetches. */}
                {isDateReady && (
                  <SalesByHours grouping='perChannel' disableFetch />
                )}
              </DashboardInnerBody>
            </DashboardInner>
          </TabPanel>
          <TabPanel value={value} index={4}>
            <DashboardInner>
              <DashboardInnerHeader
                style={{
                  justifyContent: 'flex-start'
                }}
              >
                <SubTitle>Sales per Item</SubTitle>

                <ButtonInfo
                  className='Outline ButtonInfoDrop'
                  style={{
                    marginLeft: '11px'
                  }}
                >
                  <div className='DropInfo DropInfoCenter Small'>
                    The rank and performance of your items based on Sales per
                    Item (Orders) and by Generated Revenue per Item(Sales)
                  </div>
                </ButtonInfo>
              </DashboardInnerHeader>
              <DashboardInnerBody>
                {isDateReady && <SalesByItems />}
              </DashboardInnerBody>
            </DashboardInner>
          </TabPanel>
        </>
      </DashboardContent>
    </AnalyticsFiltersProvider>
  );
};

export default Dashboard;
